import { useEffect } from "react";
import { X, MessageCircle, Phone } from "lucide-react";

const TINTS = { "ice-pink": "bg-ice-pink", "mint": "bg-mint", "sun-yellow-soft": "bg-sun-yellow-soft" };

const buildWhatsApp = (product) => {
  const text = encodeURIComponent(
    `Hi Holiday Ice Creams 🍦 — I'd like to order:\n• ${product.name} (${product.price})\nPlease share availability & delivery.`
  );
  return `https://wa.me/919235372759?text=${text}`;
};

export default function ProductModal({ product, onClose }) {
  useEffect(() => {
    if (!product) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [product, onClose]);

  if (!product) return null;
  const tintCls = TINTS[product.tint] || "bg-ice-pink";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" data-testid="product-modal">
      <div className="absolute inset-0 bg-choc/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-cream rounded-[2rem] cushion border border-[#F0E4E4]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow hover:bg-ice-pink"
          data-testid="modal-close-btn" aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid md:grid-cols-2 gap-0">
          <div className={`relative ${tintCls} aspect-square md:aspect-auto md:min-h-[400px] overflow-hidden`}>
            {product.video ? (
              <video src={product.video} autoPlay loop muted playsInline className="w-full h-full object-cover" />
            ) : (
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            )}
            <span className="absolute top-4 left-4 sticker bg-cream text-choc">{product.category.toUpperCase()}</span>
          </div>

          <div className="p-6 md:p-8 flex flex-col gap-4">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-holiday-red font-bold">Holiday Ice Creams</p>
              <h2 className="font-display text-3xl md:text-4xl mt-1">{product.name}</h2>
              <p className="font-serif text-base italic text-choc/70 mt-1">{product.tagline}</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-2xl font-bold">{product.price}</span>
              {product.badges?.map((b) => (
                <span key={b} className="sticker bg-mint text-choc">{b}</span>
              ))}
            </div>

            <p className="text-sm leading-relaxed text-choc/85">{product.desc}</p>

            <div className="grid gap-2 pt-2 mt-auto">
              <a
                href={buildWhatsApp(product)} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 bg-holiday-red text-white px-6 py-4 rounded-full font-bold hover:bg-holiday-red-deep transition-colors hover:scale-[1.02] transform"
                data-testid="modal-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5"/> Order on WhatsApp
              </a>
              <a
                href="tel:9235372759"
                className="flex items-center justify-center gap-2 bg-white border-2 border-choc text-choc px-6 py-4 rounded-full font-bold hover:bg-choc hover:text-cream transition-colors"
                data-testid="modal-call-btn"
              >
                <Phone className="w-4 h-4"/> Call 9235372759
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

